import React, {useContext, useState} from "react";
import {ActionButtonType, EnabledPolicy, GridAction} from "../../../../components/grid/GridAction";
import DeleteIcon from "@mui/icons-material/Delete";
import Api from "../../../../common/Api";
import ConfirmationDialog from "../../../../components/dialogs/ConfirmationDialog";
import {PersistentStateId} from "../../../../store/common/Grid";
import {RelationshipDto} from "../../../../common/apis/relationship/RelationshipDto";
import {RelationshipSearchType} from "../../../../common/apis/relationship/RelationshipSearchType";
import {_transl} from "../../../../store/localization/TranslMessasge";
import {createRelationshipsPanelColDef} from "./RelationshipsPanelColumnDefinition";
import ExtGridWrapper from "../../../../components/grid/ExtGridWrapper";
import {formatCommonElementName} from "../elements/CommonElementNameFormatter";
import {RelationshipCreateDialog} from "./create/RelationshipCreateDialog";
import {ElementDto} from "../../../../common/apis/element/ElementDto";
import {GridEventType} from "../../../../components/grid/GridEvents";
import EventManager from "../../../../common/event/EventManager";
import EventManagerContext from "../../../../common/event/EventManagerContext";
import Snackbar from "../snackbar/Snackbar";
import AddIcon from "@mui/icons-material/Add";
import {ElementDetailTranslationKey} from "../elementdetail/ElementDetailTranslationKey";
import RelationshipDetailDialog from "./detail/RelationshipDetailDialog";


interface RelationshipsPanelProps {
    element: ElementDto;
    onPropertyUpdate: () => void;
    relationships: Array<RelationshipDto>;
    disableRelationshipActions?: boolean;
}

export default function RelationshipsPanel(props: Readonly<RelationshipsPanelProps>) {
    const eventManager = useContext<EventManager>(EventManagerContext);

    const {element, onPropertyUpdate, relationships, disableRelationshipActions} = props;
    const [createRelationshipDialog, setCreateRelationshipDialog] = useState<boolean>(false);
    const [deleteRelationshipsDialog, setDeleteRelationshipsDialog] = useState<boolean>(false);
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
    const [detailedRelationship, setDetailedRelationship] = useState<RelationshipDto | undefined>(undefined);

    function openRelationshipDetailDialog(relationshipId: string) {
        const relationship = relationships.find(rel => rel.identifier === relationshipId);
        if (relationship) {
            setDetailedRelationship(relationship);
        }
    }

    function closeRelationshipDetailDialog() {
        setDetailedRelationship(undefined);
    }

    function openDeleteRelationshipsDialog(selectedRowIds: any[]) {
        setSelectedRowIds(selectedRowIds);
        setDeleteRelationshipsDialog(true);
    }

    function deleteSelectedRows() {
        setDeleteRelationshipsDialog(false);
        Api.relationships.deleteRelationships(selectedRowIds)
            .subscribe({
                next: () => {
                    Snackbar.success(_transl(ElementDetailTranslationKey.RELATIONSHIPS_DELETE_SUCCEEDED));
                    onPropertyUpdate();
                    eventManager.publishEvent({
                        type: GridEventType.UPDATE_SELECTED_ROWS_IDS,
                        selectedRowIds: [],
                    });
                },
                error: () => {
                    Snackbar.error(_transl(ElementDetailTranslationKey.RELATIONSHIPS_DELETE_FAILED));
                    onPropertyUpdate();
                }
            });
    }

    function getDirection(relation: RelationshipDto) {
        if (relation.source.identifier === relation.target.identifier) {
            return RelationshipSearchType.RECURSIVE;
        } else if (relation.source.identifier === element.identifier) {
            return RelationshipSearchType.SOURCE;
        } else return RelationshipSearchType.TARGET;
    }

    function getOppositeElementName(relation: RelationshipDto) {
        return relation.source.identifier === element.identifier ? formatCommonElementName(relation.target) : formatCommonElementName(relation.source);
    }

    function createTableRows(relationships: RelationshipDto[]) {
        return relationships.map(rel => ({
            identifier: rel.identifier,
            direction: getDirection(rel),
            type: rel.type,
            name: rel.name,
            description: rel.description,
            oppositeElementName: getOppositeElementName(rel),
            acl: rel.acl,
        }));
    }

    const rows = createTableRows(relationships);

    return (
        <>
            <ExtGridWrapper
                columns={createRelationshipsPanelColDef(_transl)}
                rows={rows}
                rowCount={rows.length}
                getRowId={row => row.identifier}
                actions={[
                    GridAction.detailButtonBuilder()
                        .onClick((selectedRowIds) => openRelationshipDetailDialog(selectedRowIds[0]))
                        .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                        .build(),
                    GridAction.buttonBuilder("ITEMS_RELATIONSHIP_ADD_BUTTON", ActionButtonType.IMMEDIATE, _transl(ElementDetailTranslationKey.RELATIONSHIPS_ADD_BUTTON),
                        <AddIcon/>)
                        .onClick(() => setCreateRelationshipDialog(true))
                        .enabledPolicy(EnabledPolicy.ALWAYS)
                        .isVisible(() => !disableRelationshipActions)
                        .build(),
                    GridAction.buttonBuilder("ITEMS_RELATIONSHIP_REMOVE_BUTTON", ActionButtonType.IMMEDIATE, _transl(ElementDetailTranslationKey.RELATIONSHIPS_DELETE_BUTTON),
                        <DeleteIcon/>)
                        .onClick(selectedRowIds => openDeleteRelationshipsDialog(selectedRowIds))
                        .enabledPolicy(EnabledPolicy.WHEN_AT_LEAST_ONE_SELECTED)
                        .isEnabled(selectedRows => selectedRows.every(relationship => relationship.acl.canDelete))
                        .isVisible(() => !disableRelationshipActions)
                        .build(),

                ]}
                onRowDoubleClick={(params) => openRelationshipDetailDialog(params.row.identifier)}
                peristentStateId={PersistentStateId.ELEMENT_DETAIL_PAGE_RELATIONSHIPS_GRID}
                resourceId={element.identifier} />

            {createRelationshipDialog &&
                <RelationshipCreateDialog originalElement={element}
                                          onSave={onPropertyUpdate}
                                          onClosed={() => setCreateRelationshipDialog(false)}/>
            }
            {deleteRelationshipsDialog &&
                <ConfirmationDialog open={true}
                                    title={_transl(ElementDetailTranslationKey.RELATIONSHIPS_DELETE_TITLE)}
                                    confirmationText={_transl(ElementDetailTranslationKey.RELATIONSHIPS_DELETE_CONFIRMATION)}
                                    onConfirm={() => deleteSelectedRows()}
                                    onReject={() => setDeleteRelationshipsDialog(false)}/>
            }

            {detailedRelationship &&
                <RelationshipDetailDialog open={true}
                        relationship={detailedRelationship}
                        onClose={closeRelationshipDetailDialog} />
            }
        </>
    );
}
